import { useEffect, useState } from "react";
import { AlertTriangle, FileText, Loader2, X } from "lucide-react";
import api, { fileUrl } from "@/lib/api";

export const inputClass =
  "w-full rounded-xl border border-[#E6E1D8] bg-white px-3.5 py-2.5 text-[15px] text-[#111111] outline-none transition-colors placeholder:text-[#a8a29a] focus:border-[#F2811D] focus:ring-2 focus:ring-[#F2811D]/15";

export function Field({ label, hint, children, required }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-semibold text-[#111111]">
        {label}
        {required && <span className="ml-1 text-[#F2811D]">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1.5 block text-xs leading-relaxed body-text">{hint}</span>}
    </label>
  );
}

export function Card({ children, className = "", testId }) {
  return (
    <section
      className={`rounded-2xl border border-[#E6E1D8] bg-white p-5 md:p-6 ${className}`}
      data-testid={testId}
    >
      {children}
    </section>
  );
}

const BADGE_TONES = {
  neutral: "bg-[#F4F1EB] text-[#5d574f]",
  orange: "bg-[#FDF0E1] text-[#A85A05]",
  green: "bg-[#EAF6EE] text-[#1B6B3A]",
  red: "bg-[#FDF0EF] text-[#B3261E]",
  blue: "bg-[#EDF2FB] text-[#2C4D8F]",
};

const STATUS_TONE = {
  Active: "green",
  Upcoming: "blue",
  "On hold": "orange",
  Completed: "neutral",
  Archived: "neutral",
  Submitted: "blue",
  "Changes requested": "red",
  Approved: "green",
  Paid: "green",
  Reported: "green",
  "Update pending": "orange",
  "No insights yet": "neutral",
};

export function Badge({ children, tone, testId }) {
  const resolved = tone || STATUS_TONE[children] || "neutral";
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold ${BADGE_TONES[resolved]}`}
      data-testid={testId}
    >
      {children}
    </span>
  );
}

export function Button({
  children,
  variant = "primary",
  busy,
  className = "",
  testId,
  type = "button",
  ...rest
}) {
  const styles = {
    primary: "bg-[#F2811D] text-white hover:bg-[#D96E10]",
    dark: "bg-[#111111] text-white hover:bg-[#2a2a2a]",
    outline: "border border-[#E6E1D8] bg-white text-[#111111] hover:border-[#111111]",
    danger: "border border-[#F2C2C2] bg-white text-[#B3261E] hover:bg-[#FDF3F3]",
    ghost: "text-[#6b6660] hover:text-[#111111]",
  }[variant];
  return (
    <button
      type={type}
      data-testid={testId}
      disabled={busy || rest.disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${styles} ${className}`}
      {...rest}
    >
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function Empty({ title, body, action, testId }) {
  return (
    <div
      className="rounded-2xl border border-dashed border-[#DED8CD] bg-white px-6 py-14 text-center"
      data-testid={testId}
    >
      <h3 className="text-lg font-extrabold">{title}</h3>
      {body && <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed body-text">{body}</p>}
      {action && <div className="mt-6 flex justify-center">{action}</div>}
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return (
    <div className="flex items-center gap-2.5 py-14 text-sm body-text" data-testid="loading-state">
      <Loader2 className="h-4 w-4 animate-spin" />
      {label}
    </div>
  );
}

export function ErrorNote({ message, onRetry, testId = "error-note" }) {
  if (!message) return null;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#F2C2C2] bg-[#FDF3F3] px-4 py-3 text-sm text-[#B3261E]"
      data-testid={testId}
    >
      <span className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        {message}
      </span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="font-semibold underline">
          Try again
        </button>
      )}
    </div>
  );
}

export function Note({ children, tone = "neutral", testId }) {
  const tones = {
    neutral: "border-[#E6E1D8] bg-[#FBFAF7] text-[#5d574f]",
    orange: "border-[#F3D9A8] bg-[#FDF6E7] text-[#8A5A00]",
    green: "border-[#CBE7D2] bg-[#F1F9F3] text-[#1B6B3A]",
    red: "border-[#F2C2C2] bg-[#FDF3F3] text-[#B3261E]",
  };
  return (
    <p
      className={`rounded-xl border px-4 py-3 text-sm leading-relaxed ${tones[tone]}`}
      data-testid={testId}
    >
      {children}
    </p>
  );
}

export function Modal({ open, onClose, title, children, wide, testId }) {
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#111111]/45 p-0 sm:items-center sm:p-6">
      <div
        className={`max-h-[92vh] w-full overflow-y-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl sm:p-7 ${
          wide ? "sm:max-w-3xl" : "sm:max-w-lg"
        }`}
        data-testid={testId}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <h2 className="text-xl font-extrabold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="modal-close-button"
            className="rounded-full p-1.5 text-[#6b6660] hover:bg-[#F7F4EF]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ProgressBar({ percent, capped = true }) {
  const width = percent == null ? 0 : capped ? Math.min(100, percent) : percent;
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-[#F0ECE4]">
      <div
        className="h-full rounded-full bg-[#F2811D] transition-[width] duration-500"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

/** Renders a private attachment. Images inline, other files as a download link. */
export function Attachment({ file, testId }) {
  if (!file) return <span className="text-sm body-text">No attachment</span>;
  const label = file.label || file.original_filename;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <a
        href={fileUrl(file.id)}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={testId}
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#F2811D] hover:underline"
      >
        <FileText className="h-4 w-4" />
        {label}
      </a>
      {file.label && (
        <span className="text-[11px] body-text" data-testid={testId ? `${testId}-filename` : undefined}>
          {file.original_filename}
        </span>
      )}
    </span>
  );
}

export function SecureImage({ fileId, alt, className }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/files/${fileId}`, { responseType: "blob" });
        url = URL.createObjectURL(response.data);
        if (!cancelled) setSrc(url);
      } catch (e) {
        /* the link fallback still works */
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId]);

  if (!src)
    return <div className={`animate-pulse rounded-lg bg-[#F0ECE4] ${className}`} aria-hidden />;
  return <img src={src} alt={alt} className={className} />;
}
